import React, { createContext, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const EnvironmentContext = createContext({
  API_BASE_URL: '',
  WEBSOCKET_URL: '/socket.io/',
  userId: null,
  updateUserId: () => {}
});

export const useEnvironment = () => {
  return useContext(EnvironmentContext);
};

export const EnvironmentProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);

  // Function to get the correct user ID
  const determineUserId = () => {
    // PRIORITY 1: Always use authenticated user ID if available
    const authenticatedId = localStorage.getItem('skriboUserId');
    
    if (authenticatedId) {
      console.log("Using authenticated userId:", authenticatedId);
      return authenticatedId;
    }
    
    // PRIORITY 2: Fall back to existing anonymous ID if present
    const anonymousId = localStorage.getItem('user_id');
    if (anonymousId) {
      console.log("Using existing anonymous userId:", anonymousId);
      return anonymousId;
    }
    
    // PRIORITY 3: Create new anonymous ID as last resort
    const newAnonymousId = uuidv4();
    localStorage.setItem('user_id', newAnonymousId);
    console.log("Created new anonymous userId:", newAnonymousId);
    return newAnonymousId;
  };

  useEffect(() => {
    // Set initial user ID
    const id = determineUserId();
    console.log("Setting userId:", id);
    setUserId(id);
    
    // Listen for storage events (for cross-tab login/logout)
    const handleStorageChange = (e) => {
      // If skriboUserId changes (login/logout), update our state
      if (e.key === 'skriboUserId') {
        if (e.newValue) {
          console.log("Storage event: User logged in with ID:", e.newValue);
          setUserId(e.newValue);
        } else {
          console.log("Storage event: User logged out, falling back to anonymous ID");
          const fallbackId = localStorage.getItem('user_id') || uuidv4();
          if (!localStorage.getItem('user_id')) {
            localStorage.setItem('user_id', fallbackId);
          }
          setUserId(fallbackId);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Function to update userId - will be used by the login/logout system
  const updateUserId = (newUserId) => {
    console.log("Explicitly updating userId to:", newUserId);
    setUserId(newUserId);
    
    // If this is part of a login flow, we don't need to store in user_id
    // as skriboUserId will be set by authService
    // If this is a logout flow, we should fall back to anonymous ID
    if (!newUserId) {
      const fallbackId = localStorage.getItem('user_id') || uuidv4();
      if (!localStorage.getItem('user_id')) {
        localStorage.setItem('user_id', fallbackId);
      }
      setUserId(fallbackId);
    }
  };

  const config = {
    API_BASE_URL: process.env.NODE_ENV === 'production' 
      ? 'https://skribo-api-d3bkebfdepdqera3.northeurope-01.azurewebsites.net/api'
      : 'http://localhost:5000/api',
    WEBSOCKET_URL: process.env.NODE_ENV === 'production'
      ? 'wss://skribo-api-d3bkebfdepdqera3.northeurope-01.azurewebsites.net'
      : 'http://localhost:5000',
    userId,
    updateUserId
  };

  return (
    <EnvironmentContext.Provider value={config}>
      {children}
    </EnvironmentContext.Provider>
  );
};